import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ThemeProvider as StyledThemeProvider } from 'styled-components';

import { useAppSelector } from '../store';
import { themes, ThemeMode } from './theme';

const MEDIA_QUERY = '(prefers-color-scheme: dark)';

const getSystemMode = (): ThemeMode => {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return 'light';
  }
  return window.matchMedia(MEDIA_QUERY).matches ? 'dark' : 'light';
};

interface AppThemeProviderProps {
  children: ReactNode;
}

export const AppThemeProvider = ({ children }: AppThemeProviderProps) => {
  const preference = useAppSelector((state) => state.theme.preference);
  const [systemMode, setSystemMode] = useState<ThemeMode>(getSystemMode);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(MEDIA_QUERY);
    const handler = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };
    mql.addEventListener?.('change', handler);
    return () => mql.removeEventListener?.('change', handler);
  }, []);

  const effectiveMode: ThemeMode =
    preference === 'system' ? systemMode : preference;
  const theme = useMemo(() => themes[effectiveMode], [effectiveMode]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = effectiveMode;
    document.documentElement.style.colorScheme = effectiveMode;

    const meta = document.querySelector(
      'meta[name="theme-color"]',
    ) as HTMLMetaElement | null;
    if (meta) {
      meta.content = theme.colors.bgPage;
    }
  }, [effectiveMode, theme.colors.bgPage]);

  return <StyledThemeProvider theme={theme}>{children}</StyledThemeProvider>;
};
